export const MERRY_CHAT_MINIMAP_COMPONENT_ID = "627:73909";
export const MERRY_CHAT_MINIMAP_PREVIEW_NODE_ID = "627:73937";

export const MERRY_CHAT_MINIMAP_STATE_ORDER = ["off", "on"] as const;

export type MerryChatMinimapState =
  (typeof MERRY_CHAT_MINIMAP_STATE_ORDER)[number];

export interface MerryChatMinimapVariant {
  height: 198 | 200;
  nodeId: string;
  width: 24 | 48;
}

const variants = {
  off: {
    height: 198,
    nodeId: "627:73910",
    width: 24,
  },
  on: {
    height: 200,
    nodeId: "627:73908",
    width: 48,
  },
} satisfies Record<MerryChatMinimapState, MerryChatMinimapVariant>;

export const MERRY_CHAT_MINIMAP_VIEWPORT_HEIGHT = 200;
export const MERRY_CHAT_MINIMAP_VIEWPORT_WIDTH = 48;
export const MERRY_CHAT_MINIMAP_MARKER_HEIGHT = 2;
export const MERRY_CHAT_MINIMAP_MARKER_GAP = 12;
export const MERRY_CHAT_MINIMAP_MARKER_PITCH =
  MERRY_CHAT_MINIMAP_MARKER_HEIGHT + MERRY_CHAT_MINIMAP_MARKER_GAP;
export const MERRY_CHAT_MINIMAP_MAX_CENTERED_ITEMS = 15;
export const MERRY_CHAT_MINIMAP_PREVIEW_HEIGHT = 64;

export type MerryChatMinimapMarkerLevel = 0 | 1 | 2 | 3;

export interface MerryChatMinimapMarkerGeometry {
  height: 2 | 4;
  width: 12 | 24 | 36 | 48;
}

export interface MerryChatMinimapAnchorLayout {
  height: number;
  markerCenterY: number;
}

const markerGeometry = {
  0: { height: 4, width: 48 },
  1: { height: 2, width: 36 },
  2: { height: 2, width: 24 },
  3: { height: 2, width: 12 },
} satisfies Record<
  MerryChatMinimapMarkerLevel,
  MerryChatMinimapMarkerGeometry
>;

interface MerryChatMinimapPointerInput {
  itemCount: number;
  pointerOffsetY: number;
  scrollTop: number;
  viewportHeight?: number;
}

interface MerryChatMinimapPreviewPositionInput {
  index: number;
  itemCount: number;
  scrollTop: number;
  viewportHeight?: number;
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum);
}

function normalizeItemCount(itemCount: number) {
  return Math.max(0, Math.trunc(itemCount));
}

export function getMerryChatMinimapVariant(
  state: MerryChatMinimapState,
): MerryChatMinimapVariant {
  return variants[state];
}

export function getMerryChatMinimapContentHeight(itemCount: number) {
  const count = normalizeItemCount(itemCount);

  if (count === 0) {
    return 0;
  }

  return (
    count * MERRY_CHAT_MINIMAP_MARKER_HEIGHT +
    (count - 1) * MERRY_CHAT_MINIMAP_MARKER_GAP
  );
}

export function getMerryChatMinimapContentOffset(
  itemCount: number,
  viewportHeight = MERRY_CHAT_MINIMAP_VIEWPORT_HEIGHT,
) {
  return Math.max(
    (viewportHeight - getMerryChatMinimapContentHeight(itemCount)) / 2,
    0,
  );
}

export function getMerryChatMinimapMarkerLevel(
  markerIndex: number,
  activeIndex: number | null,
): MerryChatMinimapMarkerLevel {
  if (activeIndex === null) {
    return 3;
  }

  return Math.min(
    Math.abs(markerIndex - activeIndex),
    3,
  ) as MerryChatMinimapMarkerLevel;
}

export function getMerryChatMinimapMarkerGeometry(
  markerIndex: number,
  activeIndex: number | null,
) {
  return markerGeometry[
    getMerryChatMinimapMarkerLevel(markerIndex, activeIndex)
  ];
}

export function getMerryChatMinimapAnchorLayout(
  index: number,
  itemCount: number,
  viewportHeight = MERRY_CHAT_MINIMAP_VIEWPORT_HEIGHT,
): MerryChatMinimapAnchorLayout {
  const count = normalizeItemCount(itemCount);

  if (count === 0) {
    return { height: 0, markerCenterY: 0 };
  }

  if (count === 1) {
    return {
      height: viewportHeight,
      markerCenterY: viewportHeight / 2,
    };
  }

  const safeIndex = clamp(Math.trunc(index), 0, count - 1);
  const contentOffset = getMerryChatMinimapContentOffset(
    count,
    viewportHeight,
  );
  const markerCenter = MERRY_CHAT_MINIMAP_MARKER_HEIGHT / 2;
  const edgeHeight =
    contentOffset + markerCenter + MERRY_CHAT_MINIMAP_MARKER_PITCH / 2;

  if (safeIndex === 0) {
    return {
      height: edgeHeight,
      markerCenterY: contentOffset + markerCenter,
    };
  }

  if (safeIndex === count - 1) {
    return {
      height: edgeHeight,
      markerCenterY: MERRY_CHAT_MINIMAP_MARKER_PITCH / 2,
    };
  }

  return {
    height: MERRY_CHAT_MINIMAP_MARKER_PITCH,
    markerCenterY: MERRY_CHAT_MINIMAP_MARKER_PITCH / 2,
  };
}

export function getMerryChatMinimapNearestVisibleIndex({
  itemCount,
  pointerOffsetY,
  scrollTop,
  viewportHeight = MERRY_CHAT_MINIMAP_VIEWPORT_HEIGHT,
}: MerryChatMinimapPointerInput): number | null {
  const count = normalizeItemCount(itemCount);

  if (count === 0) {
    return null;
  }

  const contentOffset = getMerryChatMinimapContentOffset(
    count,
    viewportHeight,
  );
  const markerCenter = MERRY_CHAT_MINIMAP_MARKER_HEIGHT / 2;
  const contentPointerY =
    clamp(pointerOffsetY, 0, viewportHeight) + scrollTop - contentOffset;
  const nearestIndex = Math.round(
    (contentPointerY - markerCenter) / MERRY_CHAT_MINIMAP_MARKER_PITCH,
  );
  const firstVisibleIndex = clamp(
    Math.ceil(
      (scrollTop - contentOffset - markerCenter) /
        MERRY_CHAT_MINIMAP_MARKER_PITCH,
    ),
    0,
    count - 1,
  );
  const lastVisibleIndex = clamp(
    Math.floor(
      (scrollTop + viewportHeight - contentOffset - markerCenter) /
        MERRY_CHAT_MINIMAP_MARKER_PITCH,
    ),
    0,
    count - 1,
  );

  if (firstVisibleIndex > lastVisibleIndex) {
    return clamp(nearestIndex, 0, count - 1);
  }

  return clamp(nearestIndex, firstVisibleIndex, lastVisibleIndex);
}

export function getMerryChatMinimapPreviewCenterY({
  index,
  itemCount,
  scrollTop,
  viewportHeight = MERRY_CHAT_MINIMAP_VIEWPORT_HEIGHT,
}: MerryChatMinimapPreviewPositionInput) {
  const count = normalizeItemCount(itemCount);

  if (count === 0) {
    return viewportHeight / 2;
  }

  const safeIndex = clamp(Math.trunc(index), 0, count - 1);
  const contentOffset = getMerryChatMinimapContentOffset(
    count,
    viewportHeight,
  );
  const markerCenter = MERRY_CHAT_MINIMAP_MARKER_HEIGHT / 2;
  const anchorCenter =
    contentOffset +
    markerCenter +
    safeIndex * MERRY_CHAT_MINIMAP_MARKER_PITCH -
    scrollTop;
  const previewRadius = MERRY_CHAT_MINIMAP_PREVIEW_HEIGHT / 2;

  return clamp(anchorCenter, previewRadius, viewportHeight - previewRadius);
}
